import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Pill, Package, ShieldAlert, Database, ArrowRight, Loader2, FileText, CreditCard, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { getAllProducts, getAllBundles, getAllSafetyRules } from '../../services/firestore/formularyService';
import { CF_URLS } from '../../config/api';

interface DashboardStats {
  totalProducts: number;
  activeProducts: number;
  totalBundles: number;
  activeBundles: number;
  totalSafetyRules: number;
  activeSafetyRules: number;
  lastUpdated: string | null;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="p-1 text-text-muted hover:text-accent-light transition-colors flex-shrink-0" title="Copy">
      {copied ? <Check size={14} className="text-optimal" /> : <Copy size={14} />}
    </button>
  );
}

function StripeIntegrationPanel() {
  const [open, setOpen] = useState(false);

  const STRIPE_DATA = {
    firebaseProject: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'unknown',
    region: 'us-central1',
    webhookUrl: CF_URLS.stripeWebhook,
    checkoutUrl: CF_URLS.createCheckoutSession,
    webhookEvents: ['checkout.session.completed'],
    secrets: [
      { name: 'STRIPE_SECRET_KEY', description: 'Stripe Dashboard > Developers > API Keys > Secret key', hint: 'Starts with sk_test_ (test) or sk_live_ (production)' },
      { name: 'STRIPE_WEBHOOK_SECRET', description: 'Stripe Dashboard > Developers > Webhooks > Signing secret', hint: 'Starts with whsec_' },
    ],
    setupCommands: [
      'firebase functions:secrets:set STRIPE_SECRET_KEY',
      'firebase functions:secrets:set STRIPE_WEBHOOK_SECRET',
      'firebase deploy --only functions --force',
    ],
    testCard: '4242 4242 4242 4242',
    testExpiry: '12/34',
    testCvc: '123',
    vendorPortalUrl: '/app/vendor',
    vendorPassword: 'hemologix-vendor-2024',
    orderLookupUrl: '/app/order/lookup',
  };

  return (
    <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-5 hover:bg-bg-elevated/50 transition-colors text-left"
      >
        <div className="p-2.5 rounded-lg bg-purple-50">
          <CreditCard size={20} className="text-purple-600" />
        </div>
        <div className="flex-1">
          <p className="text-lg font-semibold text-purple-600">Stripe Integration</p>
          <p className="text-sm text-text-secondary">Setup data, secrets, webhook config, and test credentials</p>
        </div>
        {open ? <ChevronUp size={18} className="text-text-muted" /> : <ChevronDown size={18} className="text-text-muted" />}
      </button>

      {open && (
        <div
          className="anim-fade-in px-5 pb-5 space-y-5 border-t border-bg-tertiary pt-5"
        >
          {/* Cloud Function Endpoints */}
          <div>
            <h4 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">Cloud Function Endpoints</h4>
            <div className="space-y-2">
              <div className="bg-bg-elevated rounded-lg p-3">
                <p className="text-xs text-text-muted mb-1">Checkout Session (POST)</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm text-accent-light break-all flex-1">{STRIPE_DATA.checkoutUrl}</code>
                  <CopyButton text={STRIPE_DATA.checkoutUrl} />
                </div>
              </div>
              <div className="bg-bg-elevated rounded-lg p-3">
                <p className="text-xs text-text-muted mb-1">Webhook Endpoint (POST) — use this in Stripe Dashboard</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm text-accent-light break-all flex-1">{STRIPE_DATA.webhookUrl}</code>
                  <CopyButton text={STRIPE_DATA.webhookUrl} />
                </div>
              </div>
              <div className="bg-bg-elevated rounded-lg p-3">
                <p className="text-xs text-text-muted mb-1">Webhook Events to Subscribe</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm text-gold">{STRIPE_DATA.webhookEvents.join(', ')}</code>
                  <CopyButton text={STRIPE_DATA.webhookEvents.join(', ')} />
                </div>
              </div>
            </div>
          </div>

          {/* Secrets */}
          <div>
            <h4 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">Firebase Secrets to Set</h4>
            <div className="space-y-2">
              {STRIPE_DATA.secrets.map((secret) => (
                <div key={secret.name} className="bg-bg-elevated rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <code className="text-sm font-bold text-purple-600">{secret.name}</code>
                    <CopyButton text={secret.name} />
                  </div>
                  <p className="text-xs text-text-secondary">{secret.description}</p>
                  <p className="text-xs text-text-muted italic mt-0.5">{secret.hint}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Setup Commands */}
          <div>
            <h4 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">Setup Commands (run in project root)</h4>
            <div className="bg-bg-elevated rounded-lg p-3 space-y-1.5">
              {STRIPE_DATA.setupCommands.map((cmd) => (
                <div key={cmd} className="flex items-center gap-2">
                  <code className="text-sm text-green-600 flex-1">$ {cmd}</code>
                  <CopyButton text={cmd} />
                </div>
              ))}
            </div>
          </div>

          {/* Test Credentials */}
          <div>
            <h4 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">Stripe Test Mode</h4>
            <div className="bg-bg-elevated rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-muted">Test Card Number</span>
                <div className="flex items-center gap-2">
                  <code className="text-sm text-gold">{STRIPE_DATA.testCard}</code>
                  <CopyButton text={STRIPE_DATA.testCard.replace(/\s/g, '')} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-muted">Expiry</span>
                <code className="text-sm text-text-primary">{STRIPE_DATA.testExpiry}</code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-muted">CVC</span>
                <code className="text-sm text-text-primary">{STRIPE_DATA.testCvc}</code>
              </div>
            </div>
          </div>

          {/* Vendor Portal */}
          <div>
            <h4 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">Vendor Portal</h4>
            <div className="bg-bg-elevated rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-muted">URL</span>
                <div className="flex items-center gap-2">
                  <code className="text-sm text-accent-light">{STRIPE_DATA.vendorPortalUrl}</code>
                  <CopyButton text={STRIPE_DATA.vendorPortalUrl} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-muted">Password</span>
                <div className="flex items-center gap-2">
                  <code className="text-sm text-gold">{STRIPE_DATA.vendorPassword}</code>
                  <CopyButton text={STRIPE_DATA.vendorPassword} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-muted">Order Lookup</span>
                <div className="flex items-center gap-2">
                  <code className="text-sm text-accent-light">{STRIPE_DATA.orderLookupUrl}</code>
                  <CopyButton text={STRIPE_DATA.orderLookupUrl} />
                </div>
              </div>
            </div>
          </div>

          {/* Firebase Info */}
          <div className="bg-bg-elevated/50 rounded-lg p-3 border border-bg-tertiary">
            <p className="text-xs text-text-muted">
              Firebase Project: <code className="text-text-secondary">{STRIPE_DATA.firebaseProject}</code> &middot; Region: <code className="text-text-secondary">{STRIPE_DATA.region}</code>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export function FormularyDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadStats() {
      try {
        const [products, bundles, rules] = await Promise.all([
          getAllProducts(),
          getAllBundles(),
          getAllSafetyRules(),
        ]);

        const allUpdates = [
          ...products.map((p) => p.updatedAt),
          ...bundles.map((b) => b.updatedAt),
          ...rules.map((r) => r.updatedAt),
        ].filter(Boolean);

        const lastUpdated = allUpdates.length > 0
          ? allUpdates.sort().reverse()[0]
          : null;

        setStats({
          totalProducts: products.length,
          activeProducts: products.filter((p) => p.status === 'active').length,
          totalBundles: bundles.length,
          activeBundles: bundles.filter((b) => b.status === 'active').length,
          totalSafetyRules: rules.length,
          activeSafetyRules: rules.filter((r) => r.isActive).length,
          lastUpdated,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    }
    loadStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">
        <p className="font-medium">Error loading dashboard</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  const cards = [
    {
      label: 'Total Products',
      value: stats?.totalProducts ?? 0,
      sub: `${stats?.activeProducts ?? 0} active`,
      icon: Pill,
      color: 'text-gold',
      bgColor: 'bg-gold/10',
      link: '/admin/products',
    },
    {
      label: 'Total Bundles',
      value: stats?.totalBundles ?? 0,
      sub: `${stats?.activeBundles ?? 0} active`,
      icon: Package,
      color: 'text-accent-light',
      bgColor: 'bg-accent/10',
      link: '/admin/products?tab=bundles',
    },
    {
      label: 'Safety Rules',
      value: stats?.totalSafetyRules ?? 0,
      sub: `${stats?.activeSafetyRules ?? 0} active`,
      icon: ShieldAlert,
      color: 'text-optimal',
      bgColor: 'bg-green-50',
      link: '/admin/products?tab=safety',
    },
    {
      label: 'Seed Data',
      value: '--',
      sub: 'Manage seed imports',
      icon: Database,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      link: '/admin/seed',
    },
  ];

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h2 className="text-3xl font-display text-gold">Formulary Dashboard</h2>
        {stats?.lastUpdated && (
          <p className="text-base text-text-muted mt-1">
            Last updated: {new Date(stats.lastUpdated).toLocaleString()}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((card, i) => (
          <div
            key={card.label}
            className="anim-fade-in-up"
            style={{ animationDelay: `${i * 0.05}s` }}
          >
            <Link
              to={card.link}
              className="block bg-bg-card border border-bg-tertiary rounded-xl p-5 hover:border-gold/30 transition-colors group"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 rounded-lg ${card.bgColor}`}>
                  <card.icon size={20} className={card.color} />
                </div>
                <ArrowRight size={16} className="text-text-muted group-hover:text-gold transition-colors" />
              </div>
              <p className="text-3xl font-bold font-display">{card.value}</p>
              <p className="text-sm text-text-muted mt-1">{card.label}</p>
              <p className="text-sm text-text-secondary mt-1">{card.sub}</p>
            </Link>
          </div>
        ))}
      </div>

      {/* Formulary Overview link */}
      <Link
        to="/admin/products"
        className="flex items-center gap-3 px-5 py-4 mb-8 bg-gold/10 border border-gold/20 rounded-xl hover:bg-gold/20 transition-colors group"
      >
        <div className="p-2.5 rounded-lg bg-gold/20">
          <FileText size={20} className="text-gold" />
        </div>
        <div className="flex-1">
          <p className="text-lg font-semibold text-gold">Formulary Overview</p>
          <p className="text-sm text-text-secondary">View all products, tiers, and pricing</p>
        </div>
        <ArrowRight size={18} className="text-text-muted group-hover:text-gold transition-colors" />
      </Link>

      {/* Quick actions */}
      <div className="bg-bg-card border border-bg-tertiary rounded-xl p-5 mb-8">
        <h3 className="text-base font-semibold text-text-secondary uppercase tracking-wider mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Link
            to="/admin/products/new"
            className="flex items-center gap-2 px-4 py-3 bg-bg-elevated rounded-lg hover:bg-bg-tertiary transition-colors text-base"
          >
            <Pill size={16} className="text-gold" />
            Add New Product
          </Link>
          <Link
            to="/admin/bundles/new"
            className="flex items-center gap-2 px-4 py-3 bg-bg-elevated rounded-lg hover:bg-bg-tertiary transition-colors text-base"
          >
            <Package size={16} className="text-accent-light" />
            Add New Bundle
          </Link>
          <Link
            to="/admin/safety/new"
            className="flex items-center gap-2 px-4 py-3 bg-bg-elevated rounded-lg hover:bg-bg-tertiary transition-colors text-base"
          >
            <ShieldAlert size={16} className="text-optimal" />
            Add Safety Rule
          </Link>
        </div>
      </div>

      {/* Stripe Integration Panel */}
      <StripeIntegrationPanel />
    </div>
  );
}
